import { type ReactNode } from 'react';
import {
  IconApps,
  IconArchive,
  IconBook,
  IconBranch,
  IconBug,
  IconBulb,
  IconCalendarClock,
  IconCheckCircle,
  IconCloud,
  IconCode,
  IconCustomerService,
  IconDashboard,
  IconDesktop,
  IconEye,
  IconFile,
  IconHistory,
  IconList,
  IconLock,
  IconLoop,
  IconMessage,
  IconMindMapping,
  IconPhone,
  IconReply,
  IconRobot,
  IconSearch,
  IconSend,
  IconSettings,
  IconSound,
  IconStop,
  IconSwap,
  IconTag,
  IconThumbUp,
  IconThunderbolt,
  IconExclamationCircle,
  IconUser,
  IconUserGroup,
  IconVoice,
} from '@arco-design/web-react/icon';
import IconNetwork from '../components/icons/IconNetwork';
import IconHeadset from '../components/icons/IconHeadset';
import IconInspect from '../components/icons/IconInspect';
import IconChartBar from '../components/icons/IconChartBar';
import IconQueue from '../components/icons/IconQueue';
import IconSampling from '../components/icons/IconSampling';
import IconTranscript from '../components/icons/IconTranscript';

/**
 * 菜单图标统一管理（单一真源）：
 * - MENU_ICONS：按「分组 code / 叶子 route」映射（旧 legacy 路径 + 新 canonical 路由并存，避免图标散落多处）。
 * - DOMAIN_ICONS：按业务域 key（Feature.domain）映射，供侧栏「域」一级分组头使用。
 * 自制 SVG 图标（Arco 无合适语义时自绘）统一收拢在 components/icons/，本表按需 import。
 */

// ---------- 叶子/分组图标（key = 旧分组 code / 叶子 route，含新 canonical 路由） ----------

export const MENU_ICONS: Record<string, ReactNode> = {
  // 根叶子
  '/dashboard': <IconDashboard />,
  '/workbench': <IconCustomerService />,
  '/workbench/tasks': <IconCheckCircle />,

  // 全渠道服务（legacy）
  channel: <IconNetwork className="arco-icon" />,
  '/calls': <IconPhone />,
  '/calls/transcripts': <IconSearch />,
  '/chat': <IconMessage />,
  '/chat/quick-replies': <IconReply />,
  '/calls/blacklist': <IconStop />,
  '/satisfaction': <IconThumbUp />,
  '/satisfaction/config': <IconSettings />,
  '/shift': <IconCalendarClock />,
  '/chatbot': <IconRobot />,

  // 智能外呼
  '/outbound': <IconSend />,

  // 智能语音机器人
  voice: <IconRobot />,
  '/bot': <IconRobot />,
  '/intent': <IconBulb />,
  '/system/flows': <IconMindMapping />,
  '/bot/voice-defaults': <IconSettings />,

  // 智能坐席辅助
  assist: <IconHeadset className="arco-icon" />,
  '/assist/config': <IconSettings />,
  '/assist/products': <IconApps />,
  '/scripts': <IconBook />,
  '/system/collect-fields': <IconList />,
  '/system/collection': <IconArchive />,

  // 智能质检
  quality: <IconInspect className="arco-icon" />,
  '/quality': <IconInspect className="arco-icon" />,
  '/quality/settings': <IconSettings />,

  // 知识库（WeKnora 为唯一知识来源）：只剩问答 / 管理 / 历史三个入口的图标。
  // 自建的问答库与全文库此前已整体下线：页面、菜单码与图标一并摘除。

  // 客户与工单
  customer: <IconUserGroup />,
  '/crm': <IconUserGroup />,
  '/tickets': <IconFile />,
  '/workflow': <IconCheckCircle />,

  // 运营分析
  ops: <IconChartBar className="arco-icon" />,
  '/report': <IconChartBar className="arco-icon" />,
  '/voc': <IconVoice />,
  '/system/voc-labels': <IconTag />,

  // 运营监控
  monitor: <IconEye />,
  '/ops-center': <IconDashboard />,
  '/ops-center/settings': <IconSettings />,
  '/monitor/screen': <IconDesktop />,

  // 智能演练
  '/coach': <IconThunderbolt />,

  // 系统管理（legacy）
  system: <IconSettings />,
  '/account': <IconUserGroup />,
  '/system/sys-settings': <IconSettings />,
  'system.call-routing': <IconSwap />,
  '/system/routes': <IconSwap />,
  '/system/ivr-menus': <IconPhone />,
  '/system/skill-groups': <IconUserGroup />,
  '/system/sip-gateways': <IconSend />,
  '/system/sip-settings': <IconSettings />,
  '/system/trunk-accounts': <IconPhone />,
  '/system/audio-files': <IconSound />,
  'system.ticket-flow': <IconMindMapping />,
  '/system/ticket-fields': <IconFile />,
  '/system/ticket-templates': <IconApps />,
  '/system/workflows': <IconThunderbolt />,
  'system.ops': <IconCloud />,
  '/system/data-sources': <IconCode />,
  '/system/egress': <IconSend />,
  '/system/audit-logs': <IconFile />,
  '/aicenter': <IconCloud />,
  '/system/diag': <IconBug />,

  // ---------- 新 canonical 路由（IA 重构后） ----------
  // 外呼
  '/outbound/jobs': <IconSend />,
  '/outbound/scenes': <IconApps />,
  '/outbound/settings': <IconSettings />,
  // 在线客服
  '/chat/sessions': <IconMessage />,
  '/chat/bot': <IconRobot />,
  '/chat/settings': <IconSettings />,
  // 呼入
  '/inbound/calls': <IconPhone />,
  '/inbound/transcripts': <IconTranscript className="arco-icon" />,
  '/inbound/blacklist': <IconStop />,
  '/inbound/ivr': <IconPhone />,
  '/inbound/sms': <IconMessage />,
  '/inbound/settings': <IconSettings />,
  // 机器人
  '/bot/robots': <IconRobot />,
  '/bot/chat-robots': <IconRobot />,
  '/bot/intents': <IconBulb />,
  // 坐席辅助
  '/assist/scripts': <IconBook />,
  '/assist/settings': <IconSettings />,
  // 智能质检
  '/quality/sampling': <IconSampling className="arco-icon" />,
  '/quality/rules': <IconSettings />,
  // 质检模板 = 规则集组合 + 权重（组合语义，与「规则与规则集」的单据设置图标区分）
  '/quality/templates': <IconApps />,
  '/quality/jobs': <IconLoop />,
  '/quality/analytics': <IconDashboard />,
  '/quality/realtime': <IconThunderbolt />,
  '/quality/flow-reports': <IconBranch />,
  // 知识库（WeKnora 为唯一知识来源）
  // 知识管理（WeKnora 的文档型知识库，含目录/文档/标签）用「文件」语义
  '/knowledge/kb': <IconFile />,
  // 知识问答 = 一问一答的对话（对话气泡语义）；问答历史 = 往回翻的记录（时钟回退语义）。
  // 与「在线客服」的 /chat（同为对话）区分：那边是接待客户的会话，这边是查知识。
  '/knowledge/chat': <IconMessage />,
  '/knowledge/chat/history': <IconHistory />,
  '/knowledge/feedback': <IconReply />,
  // 客户与工单
  '/crm/customers': <IconUserGroup />,
  '/tickets/settings': <IconSettings />,
  // 智能演练
  '/coach/history': <IconHistory />,
  '/coach/bank': <IconBook />,
  // 运营监控
  '/operations/agents': <IconUserGroup />,
  '/operations/queues': <IconQueue className="arco-icon" />,
  '/operations/cockpit': <IconDashboard />,
  '/operations/screen': <IconDesktop />,
  '/operations/team': <IconUserGroup />,
  '/operations/duty': <IconEye />,
  '/operations/incidents': <IconExclamationCircle />,
  '/operations/shifts': <IconCalendarClock />,
  '/operations/settings': <IconSettings />,
  // 客户洞察
  '/customer-insights/satisfaction': <IconThumbUp />,
  '/customer-insights/voc': <IconVoice />,
  '/customer-insights/settings': <IconSettings />,
  // 报表中心
  '/report/core': <IconChartBar className="arco-icon" />,
  '/report/ai': <IconChartBar className="arco-icon" />,
  '/report/service': <IconChartBar className="arco-icon" />,
  // AI 能力
  '/platform/ai/providers': <IconCloud />,
  '/platform/ai/prompts': <IconFile />,
  '/platform/ai/usage': <IconCode />,
  '/platform/ai/cost': <IconSettings />,
  '/platform/ai/studio': <IconThunderbolt />,
  // 通信能力
  '/platform/telephony/gateways': <IconNetwork className="arco-icon" />,
  '/platform/telephony/trunks': <IconPhone />,
  '/platform/telephony/routing': <IconSwap />,
  '/platform/telephony/audio': <IconSound />,
  '/platform/telephony/settings': <IconSettings />,
  '/platform/telephony/sms': <IconMessage />,
  // 流程中心
  '/platform/workflow/flows': <IconMindMapping />,
  '/platform/workflow/approvals': <IconThunderbolt />,
  // 数据采集
  '/platform/data-collection/fields': <IconList />,
  '/platform/data-collection/rules': <IconArchive />,
  // 用户与组织
  '/system/identity/users': <IconUserGroup />,
  '/system/identity/agents': <IconUser />,
  '/system/identity/orgs': <IconUserGroup />,
  '/system/identity/roles': <IconSettings />,
  // 集成（开放平台已整体下线，只剩外部数据源）
  '/system/integration/data-sources': <IconCode />,
  // 安全与合规
  '/system/compliance/settings': <IconLock />,
  '/system/compliance/security': <IconLock />,
  '/system/compliance/egress': <IconSend />,
  // 审计
  '/system/audit/logs': <IconFile />,
  '/system/audit/ai': <IconFile />,
  // 故障诊断
  '/system/diagnostics': <IconBug />,
  // 公告中心
  '/system/notices': <IconMessage />,
};

/** 域一级分组图标（key = Feature.domain） */
export const DOMAIN_ICONS: Record<string, ReactNode> = {
  outbound: <IconSend />,
  chat: <IconMessage />,
  inbound: <IconPhone />,
  bot: <IconRobot />,
  assist: <IconHeadset className="arco-icon" />,
  quality: <IconInspect className="arco-icon" />,
  knowledge: <IconBook />,
  ticketing: <IconUserGroup />,
  coach: <IconThunderbolt />,
  operations: <IconEye />,
  'customer-insights': <IconThumbUp />,
  analytics: <IconChartBar className="arco-icon" />,
  ai: <IconCloud />,
  telephony: <IconNetwork className="arco-icon" />,
  workflow: <IconMindMapping />,
  'data-collection': <IconArchive />,
  identity: <IconUserGroup />,
  integration: <IconApps />,
  compliance: <IconLock />,
  audit: <IconFile />,
  diagnostics: <IconBug />,
  notice: <IconMessage />,
};
